import { logger } from 'features/logger/logger';
import { isIntentionalNavigation } from './intentionalNavigationGuard';

const LEAVE_SETTLE_MS = 1_000;
const RECENT_BEFOREUNLOAD_MS = 1_000;
const STAYED_RECHECK_MS = 5_000;
const LEAVE_EVENTS = ['beforeunload', 'pagehide'] as const;

let lastBeforeUnloadAt = Number.NEGATIVE_INFINITY;

window.addEventListener('beforeunload', () => {
  lastBeforeUnloadAt = Date.now();
});

const runIfStillHere = (onStayed: () => void): void => {
  let hidden = false;
  const markHidden = (): void => {
    hidden = true;
  };

  window.addEventListener('pagehide', markHidden);

  setTimeout(() => {
    window.removeEventListener('pagehide', markHidden);

    if (!hidden && !isIntentionalNavigation()) {
      onStayed();
    }
  }, STAYED_RECHECK_MS);
};

/**
 * Logs a failure a page leave can cause, one second later: nothing when the
 * page is gone by then, `warn` when a `beforeunload` fired up to a second
 * before the call, the page started leaving within that second or an
 * intentional navigation is marked, otherwise `error` followed by `onStayed`.
 * After a `warn`, `onStayed` runs five seconds later when the page is still
 * there: no `pagehide` came in between and no intentional navigation is
 * marked (a cancelled leave, a `mailto:` or download link, a back/forward-cache
 * restore).
 */
export const logUnlessPageLeft = (
  message: string,
  error: unknown,
  onStayed?: () => void
): void => {
  let leaving = Date.now() - lastBeforeUnloadAt <= RECENT_BEFOREUNLOAD_MS;
  const markLeaving = (): void => {
    leaving = true;
  };

  for (const type of LEAVE_EVENTS) {
    window.addEventListener(type, markLeaving);
  }

  setTimeout(() => {
    for (const type of LEAVE_EVENTS) {
      window.removeEventListener(type, markLeaving);
    }

    if (leaving || isIntentionalNavigation()) {
      logger.warn(message, undefined, error as Error);

      if (onStayed) {
        runIfStillHere(onStayed);
      }

      return;
    }

    logger.error(message, undefined, error as Error);
    onStayed?.();
  }, LEAVE_SETTLE_MS);
};
